import React from 'react';
import { format, parseISO } from 'date-fns';

import useMwdTimescale from '@/apps/mwd-v2/state/selectors/useMwdTimescale';
import { ArrowPositions } from '@/apps/mwd-v2/state/types/TooltipStore';
import { WerklijnLegendLeft } from '@/apps/mwd-v2/types/legends';
import CurrentLocationXs from '@/assets/icons/CurrentLocationXs';
import IngeregeldXs from '@/assets/icons/IngeregeldXs';
import TooltipDefectRow from '@/components/defectRow/TooltipDefectRow';
import { useMaterieelEigenschappen } from '@/components/detailPanel/materieelDetails/useMaterieelEigenschappen';
import NietInzetbaarPill from '@/components/NietInzetbaarPill/NietInzetbaarPill';
import { capitalizeFirstLetter } from '@/components/search/v1/utils/helpers';
import Pill from '@/components/UI/Pill/Pill';
import { getUniekeRijBeperkingDefecten } from '@/domain/defecten/rijBeperkingen';
import usePostDefectLocaties from '@/hooks/usePostDefectLocaties';
import { MaterieelEigenschappen } from '@/queries/materieelpaneel/materieelPaneelApi';
import { useConflicten } from '@/state/selectors/useConflicten';
import useDefecten from '@/state/selectors/useDefecten';
import { Conflict } from '@/types/Conflicten';
import { Defect } from '@/types/Defecten';
import { LocatieVoorEenheden } from '@/types/LocatieVoorEenheden';
import { WerklijnInline } from '@/types/werklijnenInline';
import { tijdstipDatum } from '@/utils/dateUtils';
import { bepaalUitloopLocatie } from '@/utils/helpers';
import { materieelsoortVanEenheid, materieelsoortVanInzet } from '@/utils/werklijnenToDeeltrajecten';

import TooltipListTitle from './parts/TooltipListTitle';
import TooltipVeiligheidsConflicten from './parts/TooltipVeiligheidsConflicten';

import styles from './MwdTooltip.module.css';

interface TooltipInformationProps {
  werklijn: WerklijnLegendLeft;
  agendaInregelingNotitie?: string;
  agendaInregelingMelding?: string;
  inregelingUitlooplocatie?: string;
  defecten: Defect[];
  conflicten: Conflict[];
  locatieVoorEenheid: LocatieVoorEenheden | undefined;
  materieelEigenschappen: MaterieelEigenschappen;
}

interface LegendLeftTooltipContentProps {
  data: WerklijnLegendLeft;
  arrowPosition: ArrowPositions;
  agendaInregelingNotitie?: string;
  agendaInregelingMelding?: string;
  inregelingUitlooplocatie?: string;
}

export const materieelType = (werklijn: WerklijnInline | WerklijnLegendLeft) => {
  if (!werklijn) return;
  const { eenheid } = werklijn;
  if (eenheid?.nummer === 'MANCO') {
    const inzet = materieelsoortVanInzet(werklijn.inzettenEnClaims[0]);
    return inzet === 'GEEN-0' ? '' : inzet;
  }
  const eenheidType = materieelsoortVanEenheid(eenheid);
  return eenheidType === 'GEEN-0' ? '' : eenheidType;
};

const omloop = (werklijn: WerklijnInline | WerklijnLegendLeft) => {
  const oorspronkelijkeDienst = werklijn.inzettenEnClaims[0]?.oorspronkelijkeDienst;
  return oorspronkelijkeDienst?.groepcode
    ? `${oorspronkelijkeDienst.groepcode}-${oorspronkelijkeDienst.volgnummer}`
    : '';
};

const inregeling = (
  werklijn: WerklijnInline | WerklijnLegendLeft,
  className: string,
  agendaInregelingNotitie?: string,
  inregelingUitlooplocatie?: string,
  agendaInregelingMelding?: string,
) => {
  if (!werklijn || werklijn.eenheid?.nummer === 'MANCO' || !werklijn.eenheid?.inregeling) return '';
  return getInregeling(werklijn, className, agendaInregelingNotitie, inregelingUitlooplocatie, agendaInregelingMelding);
};

const TooltipInformation = ({
  werklijn,
  agendaInregelingNotitie,
  agendaInregelingMelding,
  inregelingUitlooplocatie,
  defecten,
  conflicten,
  locatieVoorEenheid,
  materieelEigenschappen,
}: TooltipInformationProps) => {
  const { end: horizonEinde } = useMwdTimescale();
  const snelheidBeperkingDefect = defecten.find((defect) => defect.snelheidBeperking);
  const rijBeperkingDefecten = getUniekeRijBeperkingDefecten(defecten);
  const snelheidBeperking = materieelEigenschappen?.snelheidBeperking || snelheidBeperkingDefect?.snelheidBeperking;
  const maxSnelheid = materieelEigenschappen?.maxSnelheid || snelheidBeperkingDefect?.standaardSnelheid?.toString();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div className={styles.legendLeftEenheidContainerStyleTooltip}>
        <div data-testid='eenheid-nummer' style={{ fontWeight: 600, display: 'flex' }}>
          {werklijn.eenheid?.nummer === 'MANCO'
            ? capitalizeFirstLetter(werklijn.eenheid?.nummer)
            : werklijn.eenheid?.nummer}
        </div>
        {materieelType(werklijn) && (
          <div data-testid='materieel-type' className={styles.legendLeftMaterieelTypeStyleTooltip}>
            {materieelType(werklijn)}
          </div>
        )}

        <div style={{ marginLeft: 'auto', marginRight: '0', paddingLeft: '20px' }}>
          <div data-testid='locatie' style={{ display: 'flex', alignItems: 'center' }}>
            {locatieVoorEenheid?.dienstregelpunt}
            {locatieVoorEenheid && !locatieVoorEenheid.stilstaande && (
              <div data-testid='rijdend-eenheid-locatie-informatie' style={{ display: 'flex', alignItems: 'center' }}>
                <CurrentLocationXs
                  data-testid='current-location-icon'
                  height='16px'
                  width='16px'
                  className={styles.legendLeftLocatieIconStyleTooltip}
                />
                <span
                  className={styles.legendLeftLocatieTimeStyleTooltip}
                >{`(${tijdstipDatum(locatieVoorEenheid.tijd)})`}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
        {snelheidBeperking && (
          <Pill variant='warning' testid='max-snelheid' className={styles.maxSnelheidPillStyle}>
            {'Max: '}
            {maxSnelheid && (
              <span
                className={styles.strikeThroughStyle}
                style={{ color: 'var(--tractie-color-content-error-inverse)' }}
              >
                {maxSnelheid}
              </span>
            )}
            <span>{`${maxSnelheid ? ' ' : ''}${snelheidBeperking}`}</span>
          </Pill>
        )}
        {rijBeperkingDefecten.map((defect) => (
          <Pill key={defect.id} variant='warning' testid='rijbeperking' className={styles.maxSnelheidPillStyle}>
            {defect.rijBeperking}
          </Pill>
        ))}
        <NietInzetbaarPill eenheid={werklijn.eenheid} horizonEinde={horizonEinde} />
        {omloop(werklijn) && (
          <div data-testid='omloop' className={styles.legendLeftOmloopPillStyleTooltip}>
            {omloop(werklijn)}
          </div>
        )}
      </div>
      <div
        data-testid='inregeling'
        className={[
          styles.legendLeftInregelingContainerStyleTooltip,
          agendaInregelingNotitie || agendaInregelingMelding ? styles.legendLeftInregelingMetNotitieStyleTooltip : '',
        ].join(' ')}
      >
        {inregeling(
          werklijn,
          styles.legendLeftInregelingIconStyleTooltip,
          agendaInregelingNotitie,
          inregelingUitlooplocatie,
          agendaInregelingMelding,
        )}
      </div>
      {defecten.length > 0 && (
        <div className={styles.legendLeftDefectContainerStyleTooltip}>
          <TooltipListTitle data-testid='defecten' title='Defecten' />
          <div className={styles.defectenContainerStyle}>
            {defecten.map((defect) => (
              <div data-testid={`tooltip-${defect.toelichting}`} style={{ display: 'flex' }} key={defect.id}>
                <TooltipDefectRow defect={defect} />
              </div>
            ))}
          </div>
        </div>
      )}
      <TooltipVeiligheidsConflicten conflicten={conflicten ?? []} />
    </div>
  );
};

const getInregeling = (
  wl: WerklijnInline | WerklijnLegendLeft,
  className: string,
  agendaInregelingNotitie?: string,
  inregelingUitlooplocatie?: string,
  agendaInregelingMelding?: string,
) => {
  const notitieText = agendaInregelingNotitie;

  return (
    <div className={styles.legendLeftInregelingStyleTooltip}>
      <IngeregeldXs data-testid='ingeregeld-icon' height='16px' width='16px' className={className} />
      <span className={styles.legendLeftInregelingDatumStyleTooltip} data-testid='inregeldatum'>
        {`Ingeregeld: ${format(parseISO(wl.eenheid.inregeling!.datum), 'dd-MM')}${
          inregelingUitlooplocatie ? ` ${inregelingUitlooplocatie}` : ''
        }`}
      </span>
      {notitieText && ' '}
      {notitieText && (
        <span className={styles.legendLeftInregelingNotitieStyleTooltip} data-unbroken={!/\s/.test(notitieText)}>
          {notitieText}
        </span>
      )}
      {agendaInregelingMelding && (
        <>
          {' '}
          <span className={styles.legendLeftInregelingNotitieStyleTooltip} role='status'>
            {agendaInregelingMelding}
          </span>
        </>
      )}
    </div>
  );
};

const LegendLeftTooltipContent: React.FC<LegendLeftTooltipContentProps> = ({
  data,
  arrowPosition,
  agendaInregelingNotitie,
  agendaInregelingMelding,
  inregelingUitlooplocatie,
}) => {
  const { defectenView } = useDefecten(
    data.eenheid?.nummer ? [data.eenheid?.nummer] : null,
    ['DEFECT_NIET_INZETBAAR', 'BEPERKING_IN_TREINDIENST', 'GEEN_BEPERKINGEN'],
    {
      geefAlleenDefectenTerugTotEindeBijsturingsPeriode: true,
      geefAlleenNietGereedGemeldeDecten: true,
      sorteerOpTijd: true,
    },
  );
  const { data: locatieVoorEenheden } = usePostDefectLocaties([data.eenheid.nummer], 60_000);
  const { conflictenView: conflicten } = useConflicten(['PERRONLENGTE'], [data.werklijnId]);
  const { eigenschappen: materieelEigenschappen } = useMaterieelEigenschappen(data.eenheid.nummer);
  const locatie = bepaalUitloopLocatie(data.eenheid.nummer, locatieVoorEenheden ?? []);

  return (
    <div
      data-testid='left-legend-tooltip'
      className={arrowPosition === 'bottom' ? styles.legendLeftBottomTooltipStyle : styles.legendLeftTopTooltipStyle}
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <TooltipInformation
          werklijn={data}
          agendaInregelingNotitie={agendaInregelingNotitie}
          agendaInregelingMelding={agendaInregelingMelding}
          inregelingUitlooplocatie={inregelingUitlooplocatie}
          defecten={defectenView.filter((defect) => defect.eenheidNummer === data.eenheid?.nummer)}
          conflicten={conflicten}
          locatieVoorEenheid={locatie}
          materieelEigenschappen={materieelEigenschappen ?? null}
        />
      </div>
    </div>
  );
};

export default LegendLeftTooltipContent;
